import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { UserAccessRoleType, UserType } from '@econtractor/types';
import * as apiUser from 'api/apiUsers';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import UserNameItem from '../UsersRowItems/UserNameItem';
import SolarIcon from 'components/icons/SolarIcon';

const userPickerDescription = (
    user: Pick<UserType, 'userEmail' | 'access' | 'teamName'>,
    dirsTeamName: string,
    t: TFunction,
): string => {
    const role = user.access?.role;
    const teamName = String(user.teamName || dirsTeamName || '').trim();
    if (role === 'manager') return t('Users.roles.manager');
    if (role === 'executor') return teamName ? `${t('team')} · ${teamName}` : t('team');
    if (role === 'seller') return t('Users.roles.seller');
    return user.userEmail || '';
};

export interface UserSelectedProps {
    value?: UserType | null;
    onChange?: (user: UserType | null) => void;
    title?: ReactNode;
    /** Елемент справа від заголовка (наприклад, «Додати себе») */
    rightElement?: ReactNode;
    /** Максимальна висота списку результатів (px) */
    maxHeight?: number;
    className?: string;
    disabled?: boolean;
    /** Кількість користувачів у відповіді пошуку */
    onPage?: number;
    /** Показати кнопку очищення вибраного користувача */
    clearable?: boolean;
    /** Поставити курсор у поле пошуку, коли користувача не вибрано */
    autoFocus?: boolean;
    /** Компактний вигляд: менший аватар, лише ім'я */
    smallMode?: boolean;
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
    /** Якщо задано — пошук лише користувачів цієї ролі */
    role?: UserAccessRoleType;
    /** Не показувати цих користувачів у результатах пошуку */
    excludeIds?: number[];
}

const UserSelected = ({
    value = null,
    onChange,
    title,
    rightElement = null,
    maxHeight = 220,
    className = '',
    disabled = false,
    onPage = 50,
    clearable = true,
    autoFocus = false,
    smallMode = false,
    preload = false,
    role,
    excludeIds,
}: UserSelectedProps) => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const searchRef = useRef<HTMLInputElement>(null);
    const requestRef = useRef(0);
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState<UserType[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const isFixed = Boolean(value);

    useEffect(() => {
        if (preload || isFixed || !autoFocus) return;
        searchRef.current?.focus();
    }, [autoFocus, isFixed, preload]);

    if (preload) return null;
    const showResults = loading || searched;
    const excluded = new Set((excludeIds || []).map((id) => Number(id)));
    const visibleUsers = users.filter((user) => !excluded.has(Number(user.ID)));

    const fetchUsers = async () => {
        if (disabled || loading || isFixed) return;

        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setLoading(true);
        const res = await apiUser.getUsers({
            search: (search || '').trim(),
            page: 1,
            onPage,
            ...(role ? { role } : {}),
        });
        if (requestRef.current !== requestId) return;
        setLoading(false);
        setSearched(true);

        if (!res) {
            setUsers([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setUsers(res.users || []);
    };

    const collapseSearch = () => {
        requestRef.current += 1;
        setLoading(false);
        setSearched(false);
        setUsers([]);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchUsers();
        }
    };

    const handleSelect = (user: UserType) => {
        if (disabled) return;
        onChange?.(user);
        setSearch('');
        setUsers([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (disabled) return;
        onChange?.(null);
        setSearch('');
        setUsers([]);
        setSearched(false);
    };

    const labelText = title || t('Users.selectTitle');

    const descriptionFor = (user: UserType) => {
        const teamId = Number(user.access?.team || 0);
        const dirsTeamName = teamId
            ? localSettings?.DIRS?.Team?.find((team) => team.ID === teamId)?.teamName || ''
            : '';
        return userPickerDescription(user, dirsTeamName, t);
    };

    return (
        <div
            className={[
                'user-selected',
                smallMode ? 'user-selected--small' : undefined,
                className || 'mb-3',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {(labelText || rightElement) && (
                <div className="d-flex justify-content-between align-items-center">
                    <label
                        className={`form-label text-muted mb-1 d-inline-flex align-items-center ${smallMode ? 'fs-12' : ''} ${labelText ? '' : 'd-none'}`}
                    >
                        {labelText}
                    </label>
                    {rightElement}
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user">
                        <UserNameItem
                            user={value!}
                            smallMode={smallMode}
                            description={descriptionFor(value!)}
                        />
                    </div>
                    {clearable && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="user-selected-clear"
                            disabled={disabled}
                            title={t('common:buttons.clear')}
                            onClick={handleClear}
                        >
                            <SolarIcon name="x-lg" />
                        </Button>
                    )}
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            ref={searchRef}
                            type="text"
                            size={smallMode ? 'sm' : undefined}
                            value={search}
                            disabled={disabled || loading}
                            placeholder={t('Users.selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {showResults ? (
                            <Button
                                type="button"
                                variant="link"
                                className="user-selected-search-close"
                                title={t('buttons.close')}
                                onClick={collapseSearch}
                            >
                                <SolarIcon name="x-lg" />
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('Users.selectSearchBtn')}
                            onClick={fetchUsers}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && visibleUsers.length === 0 && (
                                <div className="user-selected-empty">
                                    <SolarIcon name="person-x" className="fs-5 d-block mb-1 opacity-50" />
                                    {t('Users.selectNoResults')}
                                </div>
                            )}

                            {!loading && visibleUsers.length > 0 && (
                                <ul className="user-selected-list">
                                    {visibleUsers.map((user) => (
                                        <li key={user.ID}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(user)}
                                            >
                                                <UserNameItem
                                                    user={user}
                                                    smallMode={smallMode}
                                                    description={descriptionFor(user)}
                                                />
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserSelected;
